import { create } from 'zustand';

/** How long a workspace keeps polling after a local move, in milliseconds. */
export const TRIGGER_REFRESH_WINDOW_MS = 15_000;

export interface TriggerRefreshState {
  /**
   * Per-workspace deadline (`Date.now()`-comparable epoch ms) until which
   * trigger-driven changes should be polled for. Absent or past means idle.
   */
  activeUntil: Record<string, number>;
  /** (Re)arms the window for a workspace, extending it from *now*. */
  armWindow: (workspaceId: string) => void;
}

/**
 * Bridges a local, optimistic action (moving a task) to the possibility that
 * a backend trigger reacts to it asynchronously — posting a comment, making
 * another move — outside of anything the mutation's own response tells the
 * client about. Rather than polling forever "just in case" a trigger is
 * enabled, or polling only while `hasNonTerminalActivity` sees a live
 * execution, this opens a short bounded window right after a move actually
 * happens, so a trigger's follow-on writes have a chance to show up without a
 * manual refresh.
 *
 * Deliberately not `zustand/persist`: this window is scoped to a page's
 * lifetime — nobody expects a stale 15s window to survive a reload — and
 * keying it by `workspaceId` supports being on more than one workspace's
 * board across tabs.
 *
 * Active state is computed from `Date.now()` at read time rather than driven
 * by a `setTimeout` callback, so it behaves correctly under both fake and
 * real timers with no separate timer-cleanup path to get wrong.
 */
export const useTriggerRefreshStore = create<TriggerRefreshState>()((set) => ({
  activeUntil: {},
  armWindow: (workspaceId) => {
    set((state) => ({
      activeUntil: {
        ...state.activeUntil,
        [workspaceId]: Date.now() + TRIGGER_REFRESH_WINDOW_MS,
      },
    }));
  },
}));

/** Whether `workspaceId`'s trigger-refresh window is still open right now. */
export function isTriggerRefreshActive(workspaceId: string): boolean {
  const deadline = useTriggerRefreshStore.getState().activeUntil[workspaceId];
  return deadline !== undefined && Date.now() < deadline;
}

/** Hook form of {@link isTriggerRefreshActive}, reactive to store updates. */
export function useIsTriggerRefreshActive(workspaceId: string): boolean {
  return useTriggerRefreshStore((state) => {
    const deadline = state.activeUntil[workspaceId];
    return deadline !== undefined && Date.now() < deadline;
  });
}
